import _typeof from "@babel/runtime/helpers/esm/typeof";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { NIKE_DOMAIN } from '@nike/ux-tread-dotcom-constants';
import { getConsumerChoice } from '@nike/ux-tread-locale-settings';
import { cookieConstants, convenienceKeys } from './constants';
import { parseCookie, validateCode, getMarketplaceFromURL } from './helpers';
import { readClientCookie, writeClientCookie } from './read-write-client-cookie';
var COOKIE_NAME = cookieConstants.COOKIE_NAME,
    COUNTRY_KEY = cookieConstants.COUNTRY_KEY;
var queryParam = convenienceKeys.queryParam,
    ccAlias = convenienceKeys.ccAlias;
export var COOKIE_NOT_SET_WARNING = 'geoloc cookie not set';
/**
 * @memberof ux-tread-geoloc
 * Set country value of geoloc cookie via URL.
 * Intended to make development easier.
 */

export function enableGeolocOverride() {
  var cookieConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    path: '/',
    domain: NIKE_DOMAIN
  };
  var countryOverride = new URLSearchParams(window.location.search).get(queryParam);

  if (countryOverride) {
    var finalConfig = _objectSpread({
      expires: ''
    }, cookieConfig);

    writeClientCookie("".concat(COUNTRY_KEY, "=").concat(countryOverride), finalConfig);
  }
}
/**
 * @param {string} cookieValue - The value assigned to the geoloc cookie, expected to be a comma separated String.
 * @returns {GeoLocObject} the geoloc cookie parsed into an object
 */

export function parseGeoLocCookie(cookieValue) {
  var geolocObject = parseCookie(cookieValue); // alias 'cc' as 'country', to help with code readability

  Object.defineProperty(geolocObject, ccAlias, {
    configurable: true,
    get: function get() {
      return this[COUNTRY_KEY];
    }
  });
  return geolocObject;
}
/**
 * Using this function requires that your nodejs server has cookie parsing middleware.
 * restify: https://github.com/nathschmidt/restify-cookies
 * express: https://github.com/expressjs/cookie-parser
 *
 * @param {object} req - a request object which has cookies available
 * @returns {GeoLocObject|undefined} the geoloc cookie parsed into an object or undefined if not set
 */

export function parseGeoLocCookieFromRequest(req) {
  if (req && _typeof(req.cookies) !== 'object') {
    // eslint-disable-next-line no-console
    return console.warn('No cookies object found to parse');
  }

  if (req.cookies[COOKIE_NAME]) {
    return parseGeoLocCookie(req.cookies[COOKIE_NAME]);
  } // eslint-disable-next-line no-console


  return console.warn(COOKIE_NOT_SET_WARNING);
}
/**
 * @memberof ux-tread-geoloc
 * Read the geoloc cookie, parsing its comma-separated values.
 * @param {object} [req] - optional argument of request object if parsing cookies on server
 * @returns {GeoLocObject|undefined} the geoloc cookie parsed into an object or undefined if not set
 * @example <caption>Server-side usage of `readGeolocCookie`</caption>
 * // restify example
 * const restify = require('restify');
 * const CookieParser = require('restify-cookies');
 * const { readGeolocCookie } = require('@nike/ux-tread-geoloc');
 *
 * const server = restify.createServer({});
 * server.use(CookieParser.parse);
 * server.use((req, res, next) => {
 *    const geoLocObj = readGeolocCookie(req);
 *    // evaluate geoLocObj and do stuff with it
 * });
 *
 * // express example
 * const express = require('express');
 * const cookieParser = require('cookie-parser');
 * const app = express();
 * app.use(cookieParser());
 * app.use((req, res, next) => {
 *    const geoLocObj = readGeolocCookie(req);
 *    // evaluate geoLocObj and do stuff with it
 * });
 */

export function readGeolocCookie(req) {
  // NOTE js-cookie brings up potential problems when setting cookies on server, and lists how you might read them
  // but we can avoid that by just provding the parsing portion if a request object is passed to this functiion
  if (req) {
    return parseGeoLocCookieFromRequest(req);
  } // else we can assume we are client side where this implementation is relying on client side cookies to evaluate


  var cookieValue = readClientCookie();

  if (!cookieValue) {
    // eslint-disable-next-line no-console
    console.warn(COOKIE_NOT_SET_WARNING); // NOTE we don't like this but will accept it for now..
    // if a cookieValue is empty string it will evaluate to empty string return vs undefined.
    // It's probably working because it's falsy in both cases.

    return cookieValue;
  }

  return parseGeoLocCookie(cookieValue);
}
/**
 * @memberof ux-tread-geoloc
 * Access and compare the 3 values that help us decide where a user should be shopping.
 * @param {string} [marketplaceCode] - optional ISO 3166-1 alpha-2 country code, matching the marketplace in the URL
 * @returns {boolean} if true, conflicting values were discovered and we'll likely want to ask the user for additional input
 */

export function isMismatch(marketplaceCode) {
  // get the marketplace included in the URL
  var urlMarketplace = validateCode(marketplaceCode) || getMarketplaceFromURL(window.location.pathname); // get the marketplace this user has chosen previously, if any
  // example value: us/en_us

  var _getConsumerChoice = getConsumerChoice(),
      choice = _getConsumerChoice.choice;

  var chosenMarketplace = choice && choice.slice(0, 2).toUpperCase(); // compare

  if (chosenMarketplace && chosenMarketplace !== urlMarketplace) {
    return true;
  } // we need more information, so get the country code corresponding to the user's IP address


  var edgeScapeValues = readGeolocCookie() || {};
  var country = edgeScapeValues.country;
  var geolocCountryCode = country && country.toUpperCase(); // compare

  if (geolocCountryCode && geolocCountryCode !== urlMarketplace) {
    return true;
  } // no mismatched values found


  return false;
}